import { Lazy } from '../chat/lazy';
import { fromIsoString, toIsoString } from '../chat/iso-date';
import type { JsonValue } from '../serializable';
import type { SerializedWorkflowRunStep } from './types';
import type { WorkflowRunStepData } from './types';
import type { WorkflowRunStepStatus } from './types';
import type { WorkflowRun } from './workflow-run';

export type WorkflowRunStepInputs =
  | {
      t: 'single-parent';
      data: JsonValue | null;
    }
  | {
      t: 'fan-in';
      data: Record<string, JsonValue>;
    };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The upstream value behind a step's *recorded* input, whichever shape it was
 * stored in.
 *
 * A step that reached `saveRunStepInstanceResult` records the resolved value
 * directly — a pass-through node writes the value it passed on. A step that
 * failed before that never got there, so what is on the instance is still the
 * raw {@link WorkflowRunStepInputs} envelope. Readers after the fact see both
 * and cannot tell from the id which they have.
 *
 * Kept here rather than in either reader: the server resolves this to act on a
 * step, the client resolves it to explain one, and having the two disagree is
 * how a value that was plainly in the payload reads as missing.
 */
export function resolveRecordedStepInput(value: unknown): unknown {
  if (!isRecord(value)) {
    return value;
  }
  const inner =
    value['t'] === 'fan-in'
      ? value['data']
      : value['t'] === 'single-parent'
      ? value['data'] ?? null
      : value;
  // ATC kv rows wrap a scalar as `{ ty, val }`; callers want the inner value.
  if (isRecord(inner) && typeof inner['ty'] === 'string' && 'val' in inner) {
    return inner['val'];
  }
  return inner;
}

export type WorkflowRunStepHydration = {
  readonly loadRun: () => Promise<WorkflowRun | null>;
  readonly loadInputs: () => Promise<WorkflowRunStepInputs>;
  readonly loadAtcRunIds?: () => Promise<readonly string[]>;
  readonly preloadedRun?: WorkflowRun | null;
  readonly preloadedInputs?: WorkflowRunStepInputs;
  readonly preloadedAtcRunIds?: readonly string[];
};

/**
 * One recorded step for a workflow run (`workflow_run_steps`): links a run and a node,
 * with `inputId` / `outputId` pointing at `kv_store` rows scoped by `ownerOrgId`.
 */
export class WorkflowRunStep {
  private readonly _data: WorkflowRunStepData;
  readonly run: Lazy<WorkflowRun | null>;
  readonly inputs: Lazy<WorkflowRunStepInputs>;
  readonly atcRunIds: Lazy<readonly string[]>;

  private constructor(
    data: WorkflowRunStepData,
    hydration: WorkflowRunStepHydration
  ) {
    this._data = data;
    this.run = new Lazy(hydration.loadRun, hydration.preloadedRun);
    this.inputs = new Lazy(hydration.loadInputs, hydration.preloadedInputs);
    this.atcRunIds = new Lazy(
      hydration.loadAtcRunIds ?? resolveNoAtcRunsForStep,
      hydration.preloadedAtcRunIds
    );
  }

  get id(): string {
    return this._data.id;
  }

  get ownerId(): string {
    return this._data.ownerId;
  }

  get ownerOrgId(): string {
    return this._data.ownerOrgId;
  }

  get runId(): string {
    return this._data.runId;
  }

  get nodeId(): string {
    return this._data.nodeId;
  }

  get stepIndex(): number {
    return this._data.stepIndex;
  }

  get inputId(): string | null {
    return this._data.inputId;
  }

  get outputId(): string | null {
    return this._data.outputId;
  }

  get status(): WorkflowRunStepStatus {
    return this._data.status;
  }

  get createdAt(): Date {
    return this._data.createdAt;
  }

  get updatedAt(): Date {
    return this._data.updatedAt;
  }

  get runStepData(): WorkflowRunStepData {
    return { ...this._data };
  }

  serialize(): SerializedWorkflowRunStep {
    return {
      id: this._data.id,
      ownerId: this._data.ownerId,
      ownerOrgId: this._data.ownerOrgId,
      runId: this._data.runId,
      nodeId: this._data.nodeId,
      stepIndex: this._data.stepIndex,
      inputId: this._data.inputId,
      outputId: this._data.outputId,
      status: this._data.status,
      createdAt: toIsoString(this._data.createdAt),
      updatedAt: toIsoString(this._data.updatedAt),
    };
  }

  static fromSerialized(
    serialized: SerializedWorkflowRunStep,
    hydration: WorkflowRunStepHydration
  ): WorkflowRunStep {
    const data: WorkflowRunStepData = {
      id: serialized.id,
      ownerId: serialized.ownerId,
      ownerOrgId: serialized.ownerOrgId,
      runId: serialized.runId,
      nodeId: serialized.nodeId,
      stepIndex: serialized.stepIndex,
      inputId: serialized.inputId,
      outputId: serialized.outputId,
      status: serialized.status,
      createdAt: fromIsoString(serialized.createdAt),
      updatedAt: fromIsoString(serialized.updatedAt),
    };
    return new WorkflowRunStep(data, hydration);
  }
}

export async function resolveNoRunForStep(): Promise<WorkflowRun | null> {
  return null;
}

export async function resolveNoAtcRunsForStep(): Promise<readonly string[]> {
  return [];
}
